// =============================================================================
// useAuthorPhotos — biblioteka zdjęć autora per mapa (Supabase Storage).
// =============================================================================

import { useCallback, useEffect, useState } from "react";
import {
  listAuthorPhotos,
  uploadAuthorPhoto,
  removeAuthorPhoto,
  downloadAuthorPhoto,
} from "@/services/uniquePages/authorPhotos";
import type { AuthorPhoto } from "@/types/uniquePages";

export function useAuthorPhotos(mapId: string | undefined) {
  const [photos, setPhotos] = useState<AuthorPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!mapId) return;
    setLoading(true);
    try {
      setPhotos(await listAuthorPhotos(mapId));
    } finally {
      setLoading(false);
    }
  }, [mapId]);

  useEffect(() => { void load(); }, [load]);

  const upload = useCallback(
    async (files: FileList | File[]): Promise<{ ok: number; failed: number; errors: string[] }> => {
      if (!mapId) return { ok: 0, failed: 0, errors: [] };
      setUploading(true);
      const errors: string[] = [];
      let ok = 0, failed = 0;
      try {
        for (const file of Array.from(files)) {
          const res = await uploadAuthorPhoto(mapId, file);
          if (res.ok) ok++;
          else { failed++; if (res.error) errors.push(`${file.name}: ${res.error}`); }
        }
        await load();
      } finally {
        setUploading(false);
      }
      return { ok, failed, errors };
    },
    [mapId, load],
  );

  const remove = useCallback(async (path: string) => {
    await removeAuthorPhoto(path);
    await load();
  }, [load]);

  const download = useCallback((photo: AuthorPhoto) => downloadAuthorPhoto(photo), []);

  return { photos, loading, uploading, upload, remove, download, refresh: load };
}
